import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Check,
  Copy,
  Grid3X3,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  User,
  AlertTriangle,
  Info,
} from 'lucide-react';
import {
  AI_GUARDRAILS,
  AI_SAFE_DEFAULT,
  AGENCY_RISK_MATRIX,
  DELEGATION_DIMENSIONS,
  DELEGATION_LEVELS,
  composeDelegationSentence,
  lookupMatrixCell,
  type DelegationLevel,
  type Reversibility,
  type RiskBand,
} from '../data/boundedAgencyDelegation';
import { LEVELS, STAGES, type LevelId, type StageId } from '../data/fieldOfPlay';
import {
  DELEGATION_LAB_STEPS,
  STORAGE_KEY,
  canAdvance,
  createDefaultDelegationLabState,
  nextStep,
  prefillFromLevel,
  prevStep,
  readDelegationLab,
  stepIndex,
  suggestMaxLevel,
  toDelegationBrief,
  type DelegationLabState,
  type DelegationLabStep,
  type DelegationLabTension,
} from '../lib/delegationAuthorityLab';

type Accent = DelegationLevel['accent'];

const ACCENT: Record<
  Accent,
  { badge: string; bar: string; chip: string; ring: string; soft: string }
> = {
  red: {
    badge: 'bg-[var(--coral-dim)] text-[var(--coral)]',
    bar: 'bg-[var(--coral)]',
    chip: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
    ring: 'ring-red-300 border-[var(--coral)]',
    soft: 'bg-[var(--coral-dim)]',
  },
  orange: {
    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    bar: 'bg-[var(--mustard)]',
    chip: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    ring: 'ring-orange-300 border-[var(--mustard)]',
    soft: 'bg-[var(--mustard-dim)]',
  },
  amber: {
    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    bar: 'bg-[var(--mustard)]',
    chip: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    ring: 'ring-amber-300 border-[var(--mustard)]',
    soft: 'bg-[var(--mustard-dim)]',
  },
  sky: {
    badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    bar: 'bg-[var(--slate)]',
    chip: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    ring: 'ring-sky-300 border-[var(--slate)]',
    soft: 'bg-[var(--slate-dim)]',
  },
  indigo: {
    badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    bar: 'bg-[var(--slate)]',
    chip: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    ring: 'ring-indigo-300 border-[var(--slate)]',
    soft: 'bg-[var(--slate-dim)]',
  },
  emerald: {
    badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    bar: 'bg-[var(--green)]',
    chip: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
    ring: 'ring-emerald-300 border-[var(--green)]',
    soft: 'bg-[var(--green-dim)]',
  },
  teal: {
    badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    bar: 'bg-[var(--green)]',
    chip: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
    ring: 'ring-teal-300 border-[var(--green)]',
    soft: 'bg-[var(--green-dim)]',
  },
};

const STAGE_ACCENT: Record<string, { head: string; soft: string }> = {
  sky: { head: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', soft: 'bg-[var(--slate-dim)]/80' },
  indigo: { head: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', soft: 'bg-[var(--slate-dim)]/80' },
  amber: { head: 'bg-[var(--mustard-dim)] text-[var(--mustard)]', soft: 'bg-[var(--mustard-dim)]/80' },
  violet: { head: 'bg-[var(--slate-dim)] text-[var(--text-sec)]', soft: 'bg-[var(--slate-dim)]/80' },
  emerald: { head: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]', soft: 'bg-[var(--green-dim)]/80' },
  teal: { head: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]', soft: 'bg-[var(--green-dim)]/80' },
};

const RISKS: RiskBand[] = ['low', 'moderate', 'high'];
const REVERS: Reversibility[] = ['reversible', 'irreversible'];

function loadState(): DelegationLabState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return createDefaultDelegationLabState();
    const parsed = JSON.parse(raw) as Partial<DelegationLabState>;
    return { ...createDefaultDelegationLabState(), ...parsed, aiGuardrails: parsed.aiGuardrails ?? {} };
  } catch {
    return createDefaultDelegationLabState();
  }
}

function saveState(state: DelegationLabState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

const inputClass =
  'w-full bg-[var(--bg-raised)] border border-charcoal-200  px-3 py-2.5 text-sm text-charcoal-800 placeholder-charcoal-400 focus:outline-none focus:border-consulting-slate-blue-400 resize-none';

function CoordinationStrip({
  stage,
  locus,
  onStage,
  onLocus,
}: {
  stage: StageId;
  locus: LevelId;
  onStage: (s: StageId) => void;
  onLocus: (l: LevelId) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-2">
          Where is this work in the flow?
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {STAGES.map((s) => {
            const a = STAGE_ACCENT[s.accent];
            const selected = stage === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onStage(s.id)}
                className={` border p-2 text-left transition-all ${
                  selected ? `${a.soft} ring-2 ring-consulting-slate-blue-300 border-consulting-slate-blue-200` : 'bg-[var(--bg-raised)] border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${a.head} mb-1`}>
                  {s.name}
                </span>
                <span className="block text-[11px] text-charcoal-600 leading-snug">{s.question}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-2">Locus of concern</p>
        <div className="grid sm:grid-cols-3 gap-2">
          {LEVELS.map((l) => {
            const selected = locus === l.id;
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => onLocus(l.id)}
                className={` border p-3 text-left transition-all ${
                  selected
                    ? 'bg-consulting-slate-blue-50 ring-2 ring-consulting-slate-blue-300 border-consulting-slate-blue-200'
                    : 'bg-[var(--bg-raised)] border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <span className="block text-sm font-semibold text-charcoal-900">{l.name}</span>
                <span className="block text-xs text-charcoal-500 mt-0.5">{l.description}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function RiskReversibilityMatrix({
  reversibility,
  risk,
  onSelect,
}: {
  reversibility: Reversibility;
  risk: RiskBand;
  onSelect: (r: Reversibility, k: RiskBand) => void;
}) {
  const maxLevel = suggestMaxLevel(reversibility, risk);
  const cell = lookupMatrixCell(reversibility, risk);
  const accent = ACCENT[DELEGATION_LEVELS[maxLevel - 1].accent];

  return (
    <div className="space-y-4">
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 overflow-x-auto">
        <table className="w-full border-separate border-spacing-1 min-w-[320px]">
          <thead>
            <tr>
              <th className="text-left text-xs font-semibold text-charcoal-500 p-2"> </th>
              {RISKS.map((k) => (
                <th key={k} className="text-xs font-semibold text-charcoal-500 uppercase tracking-wide p-2 capitalize">
                  {k} risk
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {REVERS.map((r) => (
              <tr key={r}>
                <th className="text-left text-xs font-semibold text-charcoal-500 uppercase tracking-wide p-2 capitalize whitespace-nowrap">
                  {r}
                </th>
                {RISKS.map((k) => {
                  const c = AGENCY_RISK_MATRIX.find((m) => m.reversibility === r && m.risk === k)!;
                  const a = ACCENT[DELEGATION_LEVELS[c.maxLevel - 1].accent];
                  const isSel = reversibility === r && risk === k;
                  return (
                    <td key={k} className="p-0">
                      <button
                        type="button"
                        onClick={() => onSelect(r, k)}
                        className={`w-full h-16  border flex flex-col items-center justify-center transition-all ${a.soft} ${
                          isSel ? `ring-2 ${a.ring}` : 'border-transparent hover:border-[var(--rule)]'
                        }`}
                      >
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${a.badge}`}>
                          Level {c.maxLevel}
                        </span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={` border p-4 ${accent.soft} border-[var(--rule)]`}>
        <p className="text-sm font-medium text-charcoal-900">
          Recommended maximum: Level {maxLevel} — {DELEGATION_LEVELS[maxLevel - 1].name}
        </p>
        <p className="text-sm text-charcoal-600 mt-1">{cell.guidance}</p>
      </div>
    </div>
  );
}

function AuthorityLadder({
  selected,
  maxRecommended,
  onSelect,
}: {
  selected: number;
  maxRecommended: number;
  onSelect: (level: number) => void;
}) {
  const level = DELEGATION_LEVELS.find((l) => l.level === selected)!;
  const accent = ACCENT[level.accent];

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-6">
      <div className="space-y-1.5">
        {[...DELEGATION_LEVELS].reverse().map((l) => {
          const a = ACCENT[l.accent];
          const isSel = l.level === selected;
          const overMax = l.level > maxRecommended;
          return (
            <button
              key={l.level}
              type="button"
              onClick={() => onSelect(l.level)}
              className={`w-full text-left flex items-center gap-2 p-2.5  border transition-all ${
                isSel ? `bg-[var(--bg-raised)] ring-2 ${a.ring}` : 'bg-[var(--bg-raised)] border-[var(--rule)] hover:border-[var(--rule)]'
              }`}
            >
              <span className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${a.badge}`}>
                {l.level}
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-semibold text-charcoal-900 truncate">{l.name}</span>
                {overMax && (
                  <span className="block text-[10px] text-[var(--mustard)]">Above matrix max ({maxRecommended})</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <div className={`h-1.5 ${accent.bar}`} />
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${accent.badge}`}>
              {level.level}
            </span>
            <h3 className="text-lg font-bold text-charcoal-900">{level.name}</h3>
            <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full border ${accent.chip}`}>
              {level.role}
            </span>
          </div>
          <p className="text-sm text-charcoal-700 italic mb-4">&ldquo;{level.instructionPattern}&rdquo;</p>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs font-semibold uppercase text-charcoal-500 mb-0.5">Agency</p>
              <p className="text-charcoal-700">{level.agency}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-charcoal-500 mb-0.5">Boundary</p>
              <p className="text-charcoal-700">{level.boundary}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TensionList({ tensions }: { tensions: DelegationLabTension[] }) {
  if (tensions.length === 0) return null;
  return (
    <div className="space-y-2">
      {tensions.map((t) => (
        <div
          key={t.id}
          className={` border p-3 text-sm flex gap-2 ${
            t.severity === 'risk'
              ? 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]'
              : t.severity === 'warn'
                ? 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]'
                : 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]'
          }`}
        >
          {t.severity === 'risk' ? (
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          ) : (
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <p>{t.message}</p>
            {t.suggestedPlay && <p className="text-xs mt-1 opacity-80">Suggested: {t.suggestedPlay}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function BriefPanel({ state }: { state: DelegationLabState }) {
  const [copied, setCopied] = useState(false);
  const brief = toDelegationBrief(state);
  const sentence = composeDelegationSentence(brief);
  const reading = readDelegationLab(state);

  const copy = async () => {
    await navigator.clipboard.writeText(sentence);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-5">
      <div className="border border-consulting-slate-blue-200 bg-consulting-slate-blue-50/40 p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-consulting-slate-blue-600 mb-2">
          Delegation sentence
        </p>
        <p className="text-sm text-charcoal-800 leading-relaxed whitespace-pre-wrap">{sentence}</p>
        <button
          type="button"
          onClick={copy}
          className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-raised)] border border-charcoal-200 text-sm text-charcoal-700 hover:bg-charcoal-50"
        >
          {copied ? <Check className="h-4 w-4 text-[var(--green)]" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy brief'}
        </button>
      </div>

      {reading.observations.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-2">Field reading</p>
          <ul className="space-y-1">
            {reading.observations.map((o, i) => (
              <li key={i} className="text-sm text-charcoal-700 flex gap-2">
                <span className="text-charcoal-400">•</span>
                {o}
              </li>
            ))}
          </ul>
        </div>
      )}

      <TensionList tensions={reading.tensions} />

      {reading.suggestedPlay && reading.tensions.length === 0 && (
        <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)] p-3">
          {reading.suggestedPlay}
        </p>
      )}

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-2">Five dimensions</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {DELEGATION_DIMENSIONS.map((d) => (
            <div key={d.name} className="border border-[var(--rule)] bg-[var(--surface)] p-3">
              <p className="text-xs font-semibold text-charcoal-800">{d.name}</p>
              <p className="text-xs text-charcoal-600 mt-0.5">{d.question}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const DelegationAuthorityLab: React.FC = () => {
  const [step, setStep] = useState<DelegationLabStep>('intro');
  const [state, setState] = useState<DelegationLabState>(loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const maxRecommended = useMemo(
    () => suggestMaxLevel(state.reversibility, state.risk),
    [state.reversibility, state.risk],
  );

  const progressPct = useMemo(
    () => ((stepIndex(step) + 1) / DELEGATION_LAB_STEPS.length) * 100,
    [step],
  );

  const updateState = useCallback((updates: Partial<DelegationLabState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const goNext = useCallback(() => {
    if (!canAdvance(step, state)) return;

    if (step === 'level') {
      const prefill = prefillFromLevel(state.level);
      setState((prev) => ({
        ...prev,
        authority: prev.authority.trim() ? prev.authority : prefill.authority,
        reporting: prev.reporting.trim() ? prev.reporting : prefill.reporting,
        escalation: prev.escalation.trim() ? prev.escalation : prefill.escalation,
      }));
    }

    if (step === 'brief') {
      setState((prev) => ({ ...prev, completedAt: new Date().toISOString() }));
    }

    const n = nextStep(step);
    if (n) setStep(n);
  }, [step, state]);

  const goBack = useCallback(() => {
    const p = prevStep(step);
    if (p) setStep(p);
  }, [step]);

  const reset = useCallback(() => {
    setState(createDefaultDelegationLabState());
    setStep('intro');
  }, []);

  return (
    <div className="min-h-screen bg-[var(--surface)] py-8">
      <div className="max-w-4xl mx-auto px-4 space-y-8">
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <ShieldCheck className="h-8 w-8 text-consulting-slate-blue-600" />
            <h1 className="text-3xl font-heading font-bold text-charcoal-900">Delegation Authority Lab</h1>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border border-consulting-slate-blue-200">
              v0.1
            </span>
          </div>
          <p className="text-charcoal-600 text-sm max-w-2xl mx-auto">
            Move from vague handoff to explicit bounded agency — see authority, risk, and boundaries before you write
            the brief.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal-500">
              Step {stepIndex(step) + 1} of {DELEGATION_LAB_STEPS.length} — {DELEGATION_LAB_STEPS[stepIndex(step)]?.label}
            </span>
            <span className="font-mono text-[10px] text-charcoal-400">{Math.round(progressPct)}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden bg-charcoal-100">
            <div
              className="h-full rounded-full transition-all duration-500 bg-consulting-slate-blue-600"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className="border border-charcoal-200 bg-[var(--bg-raised)] p-6 md:p-8 shadow-sm">
          {step === 'intro' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-charcoal-900">Delegation is bounded agency</h2>
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-charcoal-400 mb-2">The wrong question</p>
                  <p className="text-lg text-charcoal-500 line-through decoration-charcoal-300">
                    Can I trust this person or AI?
                  </p>
                </div>
                <div className="md:border-l md:border-[var(--rule)] md:pl-6">
                  <p className="text-sm font-semibold uppercase tracking-wide text-primary-600 mb-2">The better question</p>
                  <p className="text-lg text-charcoal-900 font-medium">
                    What form of agency am I transferring, within what boundary, under what conditions, with what
                    feedback requirements?
                  </p>
                </div>
              </div>
              <p className="text-sm text-charcoal-600">
                This lab walks you through outcome, risk envelope, authority level, and operating constraints — then
                generates a delegation sentence you can copy to a human or AI.
              </p>
            </div>
          )}

          {step === 'outcome' && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-charcoal-900">Name the outcome and place the work</h2>
              <label className="block space-y-2">
                <span className="text-xs font-medium text-charcoal-500">Desired outcome</span>
                <textarea
                  value={state.outcome}
                  onChange={(e) => updateState({ outcome: e.target.value })}
                  rows={3}
                  className={inputClass}
                  placeholder="What result are we trying to produce? Be specific enough to know when it's done."
                />
              </label>
              <CoordinationStrip
                stage={state.stage}
                locus={state.locus}
                onStage={(s) => updateState({ stage: s })}
                onLocus={(l) => updateState({ locus: l })}
              />
            </div>
          )}

          {step === 'risk' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-charcoal-900">Set the risk envelope</h2>
              <p className="text-sm text-charcoal-600">
                How reversible is the work, and what is the cost of being wrong? The matrix recommends a maximum
                delegation level.
              </p>
              <RiskReversibilityMatrix
                reversibility={state.reversibility}
                risk={state.risk}
                onSelect={(r, k) => {
                  const max = suggestMaxLevel(r, k);
                  updateState({
                    reversibility: r,
                    risk: k,
                    level: state.level > max ? max : state.level,
                  });
                }}
              />
            </div>
          )}

          {step === 'level' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-charcoal-900">Choose the authority level</h2>
              <p className="text-sm text-charcoal-600">
                Matrix recommends up to Level {maxRecommended}. Higher rungs transfer more decision authority.
              </p>
              <AuthorityLadder
                selected={state.level}
                maxRecommended={maxRecommended}
                onSelect={(level) => updateState({ level })}
              />
              <TensionList tensions={readDelegationLab(state).tensions.filter((t) => t.id === 'level-exceeds-matrix')} />
            </div>
          )}

          {step === 'envelope' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-charcoal-900">Define the operating envelope</h2>
              <div className="grid gap-4">
                <label className="block space-y-2">
                  <span className="text-xs font-medium text-charcoal-500">Authority — what may they do?</span>
                  <textarea
                    value={state.authority}
                    onChange={(e) => updateState({ authority: e.target.value })}
                    rows={2}
                    className={inputClass}
                  />
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-medium text-charcoal-500">Boundaries — what may they not do?</span>
                  <textarea
                    value={state.boundaries}
                    onChange={(e) => updateState({ boundaries: e.target.value })}
                    rows={2}
                    className={inputClass}
                    placeholder="Budget limits, communication limits, scope limits…"
                  />
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-medium text-charcoal-500">Escalation triggers</span>
                  <textarea
                    value={state.escalation}
                    onChange={(e) => updateState({ escalation: e.target.value })}
                    rows={2}
                    className={inputClass}
                  />
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-medium text-charcoal-500">Reporting expectation</span>
                  <textarea
                    value={state.reporting}
                    onChange={(e) => updateState({ reporting: e.target.value })}
                    rows={2}
                    className={inputClass}
                  />
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-medium text-charcoal-500">Definition of done</span>
                  <textarea
                    value={state.definitionOfDone}
                    onChange={(e) => updateState({ definitionOfDone: e.target.value })}
                    rows={2}
                    className={inputClass}
                  />
                </label>
              </div>
            </div>
          )}

          {step === 'delegate' && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-charcoal-900">Human or AI delegate?</h2>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => updateState({ delegate: 'human' })}
                  className={`flex-1 flex items-center justify-center gap-2 p-4  border transition-all ${
                    state.delegate === 'human'
                      ? 'ring-2 ring-consulting-slate-blue-300 border-consulting-slate-blue-200 bg-consulting-slate-blue-50'
                      : 'border-[var(--rule)] hover:border-[var(--rule)]'
                  }`}
                >
                  <User className="h-5 w-5" />
                  <span className="font-medium">Human</span>
                </button>
                <button
                  type="button"
                  onClick={() => updateState({ delegate: 'ai' })}
                  className={`flex-1 flex items-center justify-center gap-2 p-4  border transition-all ${
                    state.delegate === 'ai'
                      ? 'ring-2 ring-consulting-slate-blue-300 border-consulting-slate-blue-200 bg-consulting-slate-blue-50'
                      : 'border-[var(--rule)] hover:border-[var(--rule)]'
                  }`}
                >
                  <Bot className="h-5 w-5" />
                  <span className="font-medium">AI</span>
                </button>
              </div>

              {state.delegate === 'ai' && (
                <div className="space-y-4">
                  <p className="text-sm text-charcoal-600 bg-[var(--mustard-dim)] border border-[var(--mustard)] p-3">
                    {AI_SAFE_DEFAULT}
                  </p>
                  <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-500">
                    AI guardrails checklist
                  </p>
                  <div className="space-y-2">
                    {AI_GUARDRAILS.map((g) => (
                      <label key={g} className="flex items-start gap-2 text-sm text-charcoal-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={state.aiGuardrails[g] ?? false}
                          onChange={(e) =>
                            updateState({
                              aiGuardrails: { ...state.aiGuardrails, [g]: e.target.checked },
                            })
                          }
                          className="mt-1 rounded border-charcoal-300"
                        />
                        {g}
                      </label>
                    ))}
                  </div>
                  <TensionList
                    tensions={readDelegationLab(state).tensions.filter((t) => t.id === 'ai-full-autonomy-high-stakes')}
                  />
                </div>
              )}
            </div>
          )}

          {step === 'brief' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-consulting-slate-blue-600" />
                <h2 className="text-xl font-semibold text-charcoal-900">Your delegation brief</h2>
              </div>
              <BriefPanel state={state} />
            </div>
          )}

          {step === 'complete' && (
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Check className="h-6 w-6 text-[var(--green)]" />
                <h2 className="text-xl font-semibold text-charcoal-900">Brief saved locally</h2>
              </div>
              <BriefPanel state={state} />
              <div className="flex flex-wrap gap-3 pt-2">
                <Link
                  to="/elevation/bounded-agency-delegation"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-charcoal-200 text-sm font-medium text-charcoal-700 hover:bg-charcoal-50"
                >
                  Explore the full model
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/elevation/field-of-play"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-charcoal-200 text-sm font-medium text-charcoal-700 hover:bg-charcoal-50"
                >
                  <Grid3X3 className="h-4 w-4" />
                  Place work on the Field of Play
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 'intro'}
            className="inline-flex items-center gap-2 px-4 py-2 border border-charcoal-200 text-sm font-medium text-charcoal-700 hover:bg-charcoal-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm text-charcoal-500 hover:text-charcoal-700"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>
          {step !== 'complete' ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance(step, state)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {step === 'brief' ? 'Finish' : 'Continue'}
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium"
            >
              Start new brief
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DelegationAuthorityLab;
